// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Description,
  InlineAlert,
  InputCheckbox,
  LgPadding,
  Link,
  MdSpacing,
  Toast,
  ToastContext,
  XsSpacing,
} from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { getNextDeliveryForPrescriptionID } from '~shared/features/delivery/selectors/getNextDeliveryForPrescriptionID';
import { capitalizeFirstLetter } from '~shared/helpers/string';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { insurances } from '~shared/queries/query-keys/insurances';
import { useApplyManualCoupon } from '~shared/queries/useManualCoupons';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import { TermsWebView } from '../../shared/components/TermsWebView';
import { useGetCouponSignUpConfig } from '../queries/queries';
import { useAddCopayCard } from '../queries/useAddCopayCard';
import { CouponDetailsActionSheet } from './CouponDetailsActionSheet';

type Props = {
  autoAcceptTerms?: boolean; // only used for tests so patient confirms having read terms and conditions
  couponID: number;
  prescription: Prescription;
};

const ACCEPT_TERMS_COPY = 'By checking this box, I confirm that I meet the defined requirements above.';
const REVIEW_TERMS_ERROR =
  "Once you've reviewed all the terms and conditions you will be able to check the confirmation box to agree.";
const ADD_COPAY_CARD_ERROR = 'Please try again or contact support if the problem persists.';
const MISSING_COPAY_CARD_PARAMS_ERROR = 'Some information is missing. Please contact support if the problem persists.';

const styles = StyleSheet.create({
  contentWrapper: { flexGrow: 0 },
  /** Prevents the action sheet scroll content from expanding to fill the sheet. */
  scrollContentCompact: { flexGrow: 0 },
});

export const CouponAttestationActionSheet = ({ autoAcceptTerms, couponID, prescription }: Props) => {
  const { mutateAsync: applyManualCoupon } = useApplyManualCoupon();
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(autoAcceptTerms || false);
  const [isAcceptTermsDisabled, setIsAcceptTermsDisabled] = useState(true);
  const [addCouponError, setAddCouponError] = useState('');
  const { mutateAsync: addCopayCard } = useAddCopayCard();
  const delivery = useSelectorShared((state) =>
    getNextDeliveryForPrescriptionID(state, {
      prescriptionID: prescription.id,
    }),
  );
  const { couponConfig, error: couponSignUpConfigError } = useGetCouponSignUpConfig(couponID);

  // HACK: Pfizer has a copay card program and a GoodRx coupon program. This is a way to
  // differentiate between the two for now. Revisit and replace next time.
  const isCoupon = couponConfig?.description.toLowerCase().includes('coupon');
  const cardType = isCoupon ? 'coupon' : 'copay card';
  const showHowItWorksLink = couponConfig?.offer_description || couponConfig?.offer_details;

  const handleHowItWorksPress = () => {
    // won't occur, how it works link gated by presence of couponConfig in render method
    if (!showHowItWorksLink) return;

    setActiveActionSheet(
      <CouponDetailsActionSheet
        description={couponConfig.offer_description}
        details={couponConfig.offer_details}
      />,
    );
  };

  const handleAddCoupon = async () => {
    setIsSubmitting(true);
    setAddCouponError('');
    if (!hasAcceptedTerms) {
      setAddCouponError(REVIEW_TERMS_ERROR);
      setIsSubmitting(false);
      return;
    }

    const { ndc: prescriptionNdc, id: prescriptionID, user_id: userID } = prescription;
    const deliveryID = delivery?.id;

    if (!userID || !prescriptionNdc || !deliveryID) {
      setAddCouponError(MISSING_COPAY_CARD_PARAMS_ERROR);
      setIsSubmitting(false);
      return;
    }

    trackEvent({
      event: EVENTS.COPAY_COUPON__ADD_COUPON_PRESSED,
      params: { couponID },
    });

    const params = {
      user_id: userID,
      coupon_id: couponID,
      prescription_id: prescriptionID,
      prescription_ndc: prescriptionNdc,
      terms_agreed_to: hasAcceptedTerms,
    };
    const { insurance } = await addCopayCard({ params });

    if (!insurance) {
      setAddCouponError(ADD_COPAY_CARD_ERROR);
      setIsSubmitting(false);
      return;
    }

    const appliedCoupon = await applyManualCoupon({ deliveryID, insuranceID: insurance.id })
      .then(() => true)
      .catch(() => false);
    if (!appliedCoupon) {
      setAddCouponError(ADD_COPAY_CARD_ERROR);
    } else {
      trackEvent({
        event: EVENTS.COPAY_COUPON__APPLIED_DISCOUNT,
        params: {
          couponID,
        },
      });
      queryClient.invalidateQueries({ queryKey: insurances.fetchAll._def });
      closeActionSheet();
      addToast(<Toast>{capitalizeFirstLetter(cardType)} added and saved to your profile</Toast>);
    }
    setIsSubmitting(false);
  };

  return (
    <ActionSheetV2
      contentContainerStyle={styles.scrollContentCompact}
      title={couponConfig?.terms_title || 'Discount offer loading'}
      analyticsName="coupon_attestation"
      buttons={[
        <Button
          disabled={isSubmitting}
          key="add-coupon-button"
          label="Apply instant discount"
          loading={isSubmitting}
          loadingLabel="Applying discount"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={handleAddCoupon}
        />,
        <Button
          key="cancel-coupon-button"
          label="Cancel"
          onPress={closeActionSheet}
          type="tertiary"
        />,
      ]}
      checkbox={
        <InputCheckbox
          checkPlacement="left"
          label=""
          name="acceptCopayCardTerms"
          descriptions={
            <Description color={isAcceptTermsDisabled ? COLORS.TEXT_COLORS.DISABLED : COLORS.TEXT_COLORS.GREY}>
              {couponConfig?.terms_checkbox_text ?? ACCEPT_TERMS_COPY}
            </Description>
          }
          fullBleed
          onPress={() => {
            setHasAcceptedTerms(!hasAcceptedTerms);
            setAddCouponError('');
          }}
          noHighlight
          value={hasAcceptedTerms}
          testID="accept-copay-card-terms"
          disabled={isAcceptTermsDisabled}
        />
      }
      footnote={
        <>
          {addCouponError ? (
            <>
              <InlineAlert type="error">
                <Description fontFamily="bold">Could not add {cardType}</Description>
                <XsSpacing />
                <Description>{addCouponError}</Description>
              </InlineAlert>
              <MdSpacing />
            </>
          ) : undefined}
          <Description
            fontFamily="semibold"
            color={COLORS.TEXT_COLORS.GREY}
          >
            Please review all the terms and conditions before confirming you meet the requirements below.
          </Description>
        </>
      }
      fullscreen
    >
      <View
        style={styles.contentWrapper}
        testID="coupon-attestation-content-wrapper"
      >
        <LgPadding topPadding={SPACING.STATIC.NONE}>
          {showHowItWorksLink ? (
            <>
              <Link onPress={handleHowItWorksPress}>How it works</Link>
              <XsSpacing />
            </>
          ) : null}
          {couponSignUpConfigError ? (
            <InlineAlert type="error">
              <Description fontFamily="bold">Terms and conditions are not available</Description>
              <XsSpacing />
              <Description>Please contact support if the problem persists.</Description>
            </InlineAlert>
          ) : (
            <TermsWebView
              htmlContent={couponConfig?.terms || ''}
              onScrolledToBottom={() => {
                setIsAcceptTermsDisabled(false);
              }}
            />
          )}
        </LgPadding>
      </View>
    </ActionSheetV2>
  );
};
